/**
 * IosVoiceWarning — one-time modal for iOS Safari users before voice mode.
 *
 * Shown when navigator.userAgent contains iPad|iPhone AND the
 * localStorage flag 'mylife.iosVoiceWarningDismissed' is not set.
 *
 * Security (T-02-57): localStorage flag is UX only, not security-critical.
 */

import { useEffect, useRef } from 'react';

const DISMISSED_KEY = 'mylife.iosVoiceWarningDismissed';

/** Returns true if the current device is iOS Safari (iPad or iPhone) */
export function isIosSafari(): boolean {
  return /iPad|iPhone/.test(navigator.userAgent);
}

/** Returns true if the iOS voice warning has been dismissed by the user */
export function isIosWarningDismissed(): boolean {
  return localStorage.getItem(DISMISSED_KEY) === 'true';
}

interface IosVoiceWarningProps {
  onContinueVoice: () => void;
  onUseText: () => void;
}

export function IosVoiceWarning({ onContinueVoice, onUseText }: IosVoiceWarningProps) {
  // Trap focus inside the modal for accessibility
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Focus the first focusable element
    const el = dialogRef.current?.querySelector<HTMLElement>('button, [tabindex]');
    el?.focus();
  }, []);

  const handleContinueVoice = () => {
    localStorage.setItem(DISMISSED_KEY, 'true');
    onContinueVoice();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="ios-warning-title"
      aria-describedby="ios-warning-desc"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <div
        ref={dialogRef}
        className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 space-y-4"
      >
        <h2 id="ios-warning-title" className="text-lg font-semibold text-stone-900">
          Voice mode on iPhone
        </h2>

        <p id="ios-warning-desc" className="text-sm text-stone-600 leading-relaxed">
          Voice mode on iPhone routes audio through your device speaker, not your AirPods or
          headphones. This is an iOS limitation.
        </p>
        <p className="text-sm text-stone-600">
          Use <strong>Text mode</strong> if you'd like to use headphones.
        </p>

        <div className="flex flex-col gap-3 pt-2">
          <button
            type="button"
            onClick={handleContinueVoice}
            className="w-full py-3 px-4 rounded-xl bg-amber-500 text-white font-semibold hover:bg-amber-600 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400"
          >
            Continue with Voice
          </button>
          <button
            type="button"
            onClick={onUseText}
            className="w-full py-3 px-4 rounded-xl border border-stone-300 text-stone-700 font-medium hover:bg-stone-50 transition-colors focus:outline-none focus:ring-2 focus:ring-stone-400"
          >
            Use Text Mode Instead
          </button>
        </div>
      </div>
    </div>
  );
}
